import React from 'react';
import { Player, Card as CardData, ActivationAttempt, CardType } from '../types';
import Card from './Card';
import * as gameLogic from '../services/gameLogic';
import * as uiServices from '../services/ui';
import { isTouchDevice } from '../services/device';
import { Language } from '../data/translations';
import { canCounterblastTarget, getCurrentActivationTargetCard } from '../services/damageRules';

interface MageSpellModalProps {
    localPlayerId: number;
    players: Player[];
    activations: ActivationAttempt[];
    isOpen: boolean;
    onClose: () => void;
    onCastFromHand: (cardId: string) => void;
    setTargeting: (t: any) => void;
    setMousePos: (pos: { x: number; y: number }) => void;
    preparedCardRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    handleCardMouseEnter: (card: CardData, rotation: number, source: string) => void;
    handleCardMouseLeave: () => void;
    mageLaneYPos?: number;
    language?: Language;
}

const MageSpellModal: React.FC<MageSpellModalProps> = ({
    localPlayerId,
    players,
    activations,
    isOpen,
    onClose,
    onCastFromHand,
    setTargeting,
    setMousePos,
    preparedCardRefs,
    handleCardMouseEnter,
    handleCardMouseLeave,
    mageLaneYPos = 0,
    language = 'en'
}) => {
    if (!isOpen) return null;

    const me = players.find(p => p.id === localPlayerId)!;
    const raw = me.hand.filter(c => !!c.essenceCost && c.subType === 'Spell');
    const seen = new Set<string>();
    const cards = raw.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
    const used = me.prepared.filter(pc => pc.mageHandCast).length;
    const canMore = used < 3;
    const cardCount = cards.length;
    const spacing = 120;

    const handleBackdropClick = (e: React.PointerEvent) => {
        // Only close if clicking the backdrop itself, not the cards
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <>
            {/* Backdrop for closing modal */}
            <div
                className="fixed inset-0 z-20"
                onPointerDown={handleBackdropClick}
                style={{ touchAction: 'none' }}
            />
            <div className={`fixed left-0 w-full h-40 flex items-center justify-center p-2 z-30 transition-all duration-700 ease-in-out`} style={{ top: '50%', transform: 'translateY(-50%)' }}>
                {cards.map((c, index) => {
                    const xOffset = (index - (cardCount - 1) / 2) * spacing;
                    // Determine if it's responder turn
                    const currentActivation = activations.length > 0 ? activations[activations.length - 1] : null;
                    const responderId = currentActivation ? (currentActivation.initiatorId === 1 ? 2 : 1) : null;
                    const isResponderTurn = !!currentActivation && currentActivation.state === 'PENDING_RESPONSE' && responderId === localPlayerId;
                    // Non-reaction spells are disabled during responder turn
                    const isNonReactionOnResponderTurn = isResponderTurn && !c.extendedShield;
                    const preparedLike = { card: c, rotation: 0 } as any;
                    const eff = gameLogic.getCardEffect(preparedLike);
                    const validActivationIds = eff.type === CardType.Counterblast
                        ? activations
                            .filter(activation => !activation.resolved && !activation.accepted)
                            .filter(activation => canCounterblastTarget(getCurrentActivationTargetCard(activation), localPlayerId))
                            .map(activation => activation.attackingCard.card.id)
                        : undefined;
                    const isCounterblastWithoutTargets = eff.type === CardType.Counterblast && (validActivationIds?.length ?? 0) === 0;
                    const disabled = !canMore || (me.essence < (c.essenceCost || 0)) || isNonReactionOnResponderTurn || isCounterblastWithoutTargets;
                    const startTargeting = (e: React.PointerEvent<HTMLDivElement>) => {
                        // Cards that target activation chains (Counterblast, Invincible as extended shields from hand)
                        const activationTargetable = gameLogic.isActivationChainTargetable(eff.type);
                        if (activationTargetable && activations && activations.length > 0) {
                            if (disabled) return;
                            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                            preparedCardRefs.current[c.id] = e.currentTarget as HTMLDivElement;
                            setTargeting({ card: preparedLike, startRect: rect, source: 'mage-counter', validActivationIds } as any);
                            setMousePos({ x: e.clientX, y: e.clientY });
                            // Intentionally not closing modal immediately, as targeting mode takes over?
                            // Actually, usually we might want to close the modal or keep it open for visual context.
                            // Following original implementation: do nothing else.
                            return;
                        }

                        const targetable = gameLogic.isTargetableEffect(eff.type);
                        if (!targetable) {
                            if (!disabled) {
                                onCastFromHand(c.id);
                                onClose();
                            }
                            return;
                        }
                        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                        preparedCardRefs.current[c.id] = e.currentTarget as HTMLDivElement;
                        setTargeting({ card: preparedLike, startRect: rect, source: 'mage-hand' } as any);
                        setMousePos({ x: e.clientX, y: e.clientY });
                    };
                    return (
                        <div key={c.id} className="transition-transform duration-700" style={{ position: 'absolute', left: '50%', transform: `translateX(calc(-50% + ${xOffset}px)) scale(1.2)` }}>
                            <div onPointerDown={startTargeting} className={`p-2 rounded-xl border-2 ${disabled ? 'border-slate-700 opacity-60 cursor-not-allowed' : 'border-pink-500 hover:border-pink-300'} bg-slate-900/80 shadow-xl`} style={{ touchAction: 'none' }}>
                                <Card card={c} rotation={0} onMouseEnterApp={handleCardMouseEnter} onMouseLeaveApp={handleCardMouseLeave} language={language} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default MageSpellModal;
